import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import CustomError from "models/CustomError"
import ValidationService, { validationRules } from "services/ValidationService"
import { useUnits } from "stores/units"
import { IValidationRule } from "@/types"
import { IGetFreightUnit } from "models/Asset"
import Unit from "models/Unit"
import Form from "components/Form/Form"
import { Grid, GridItem } from "components/Grid"
import { Flex, FlexItem } from "components/Flex"
import FormField from "components/FormField/FormField"
import UnitPicker from "components/UnitPicker/UnitPicker"
import InputNumber from "components/InputNumber/InputNumber"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import { getTranslation } from "utils/translations"
import { useTenantSettings } from "stores/tenantSettings"
import useTranslationsValidation from "components/Translations/hooks/useTranslationsValidation"
import config from "config/config"
import TenantTranslations from "components/Translations/TenantTranslations"
import Button from "components/Button/Button"

interface IFreightUnitModalProps {
    freightUnit?: IGetFreightUnit
    onSave(data: IGetFreightUnit): void
    onClose(): void
}

const FreightUnitModal: FC<IFreightUnitModalProps> = ({
    freightUnit,
    onSave,
    onClose,
}) => {
    const { t } = useTranslation()
    const { units, getUnit } = useUnits()
    const { tenantPrimaryLanguageIsoCode } = useTenantSettings()
    const mandatoryTranslationIsoCodes = [tenantPrimaryLanguageIsoCode || config.defaultLanguage]
    const [translations, setTranslations] = useState(freightUnit?.translations ?? {})
    const [showOnlyRequiredLanguages, setShowOnlyRequiredLanguages] = useState(true)
    const [measurementSystemId, setMeasurementSystemId] = useState(freightUnit?.measurementSystemId ?? units[0].id)
    const [dimension1, dimension1Number, setDimension1] = useInputNumber(freightUnit?.dimension1 ?? "")
    const [dimension2, dimension2Number, setDimension2] = useInputNumber(freightUnit?.dimension2 ?? "")
    const [dimension3, dimension3Number, setDimension3] = useInputNumber(freightUnit?.dimension3 ?? "")
    const [volume, volumeNumber, setVolume] = useInputNumber(freightUnit?.volume ?? "")
    const [weight, weightNumber, setWeight] = useInputNumber(freightUnit?.weight ?? "")

    const [isSubmitted, setIsSubmitted] = useState(false)

    const currentUnit = getUnit(measurementSystemId) || units[0]

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        const validationFields: [string, IValidationRule, any][] = [
            ["measurementSystemId", validationRules.freightUnit.measurementSystemId, measurementSystemId],
            ["dimension1", validationRules.freightUnit.dimension1, dimension1],
            ["dimension2", validationRules.freightUnit.dimension2, dimension2],
            ["dimension3", validationRules.freightUnit.dimension3, dimension3],
            ["volume", validationRules.freightUnit.volume, volume],
            ["weight", validationRules.freightUnit.weight, weight],
        ]
        return ValidationService.validateMultiple(validationFields)
    }, [dimension1, dimension2, dimension3, measurementSystemId, volume, weight])

    const descriptionValidationErrors = useTranslationsValidation(translations, mandatoryTranslationIsoCodes)

    async function handleSave()  {
        setIsSubmitted(true)

        if (Object.keys(descriptionValidationErrors).length > 0
            || Object.keys(validationErrors).length > 0) {
            return
        }

        onSave({
            description: getTranslation(translations, tenantPrimaryLanguageIsoCode),
            translations,
            measurementSystemId,
            dimension1: dimension1Number,
            dimension2: dimension2Number,
            dimension3: dimension3Number,
            volume: volumeNumber,
            weight: weightNumber,
        })
        onClose()
    }

    function handleUnitChange(unit: Unit) {
        setDimension1("")
        setDimension2("")
        setDimension3("")
        setMeasurementSystemId(unit.id)
    }

    return (
        <ConfirmModal
            title={freightUnit
                ? t('freightUnit.editFreightUnit')
                : t('freightUnit.addFreightUnit')
            }
            confirmButtonLabel={t("common.save")}
            maxWidth="708px"
            onClose={onClose}
            onConfirm={handleSave}>
            <Form onSubmit={handleSave}>
                <Grid columns="1fr" gap="2.4rem">
                    <GridItem className="border-bottom-grey-100 pb-3">
                        <Flex justifyContent="space-between" alignItems="center" gap="1.6rem" className="mb-2">
                            <FlexItem className="font-bold">{t('common.description')}</FlexItem>
                            <FlexItem>
                                <Button
                                    theme="link"
                                    label={showOnlyRequiredLanguages ? t('common.showTranslations') : t('common.hideTranslations')}
                                    onClick={() => setShowOnlyRequiredLanguages(current => !current)}
                                />
                            </FlexItem>
                        </Flex>
                        <TenantTranslations
                            translations={translations}
                            onChange={setTranslations}
                            validationErrors={isSubmitted ? descriptionValidationErrors : undefined}
                            mandatoryTranslationIsoCodes={mandatoryTranslationIsoCodes}
                            showOnlyRequiredLanguages={showOnlyRequiredLanguages}
                        />
                    </GridItem>
                    <GridItem>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center">
                            <FormField label={t('common.dimensionsLengthWidthHeight')} />
                            <UnitPicker
                                unitId={currentUnit.id}
                                onChange={handleUnitChange}
                            />
                        </Flex>
                        <Grid columns="1fr 2.4rem 1fr 2.4rem 1fr">
                            <GridItem>
                                <FormField optional>
                                    <InputNumber
                                        value={dimension1}
                                        onChange={setDimension1}
                                        maxDecimals={2}
                                        before={<div className="px-1">{t('common.dimensionShort.length')}</div>}
                                        after={<div className="pl-1 pr-2">{currentUnit.printShort()}</div>}
                                        isInvalid={ValidationService.isFieldInvalid(validationErrors, 'dimension1')}
                                        isSubmitted={isSubmitted}
                                    />
                                </FormField>
                                {isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'dimension1') && (
                                    <ValidationMessage>
                                        {validationErrors.dimension1.message}
                                    </ValidationMessage>
                                )}
                            </GridItem>
                            <GridItem className="box-between-fields flex-center" aria-hidden>x</GridItem>
                            <GridItem>
                                <FormField optional>
                                    <InputNumber
                                        value={dimension2}
                                        onChange={setDimension2}
                                        maxDecimals={2}
                                        before={<div className="px-1">{t('common.dimensionShort.width')}</div>}
                                        after={<div className="pl-1 pr-2">{currentUnit.printShort()}</div>}
                                        isInvalid={ValidationService.isFieldInvalid(validationErrors, 'dimension2')}
                                        isSubmitted={isSubmitted}
                                    />
                                </FormField>
                                {isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'dimension2') && (
                                    <ValidationMessage>
                                        {validationErrors.dimension2.message}
                                    </ValidationMessage>
                                )}
                            </GridItem>
                            <GridItem className="box-between-fields flex-center" aria-hidden>x</GridItem>
                            <GridItem>
                                <FormField optional>
                                    <InputNumber
                                        value={dimension3}
                                        onChange={setDimension3}
                                        maxDecimals={2}
                                        before={<div className="px-1">{t('common.dimensionShort.height')}</div>}
                                        after={<div className="pl-1 pr-2">{currentUnit.printShort()}</div>}
                                        isInvalid={ValidationService.isFieldInvalid(validationErrors, 'dimension3')}
                                        isSubmitted={isSubmitted}
                                    />
                                </FormField>
                                {isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'dimension3') && (
                                    <ValidationMessage>
                                        {validationErrors.dimension3.message}
                                    </ValidationMessage>
                                )}
                            </GridItem>
                        </Grid>
                    </GridItem>
                    <Grid columns="1fr 1fr" gap="2.4rem">
                        <GridItem>
                            <FormField
                                label={t("common.volume")}
                                optional>
                                <InputNumber
                                    value={volume}
                                    onChange={setVolume}
                                    maxDecimals={2}
                                    after={<div className="pl-1 pr-2">{t("common.m3")}</div>}
                                    isInvalid={ValidationService.isFieldInvalid(validationErrors, 'volume')}
                                    isSubmitted={isSubmitted}
                                />
                            </FormField>
                            {isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'volume') && (
                                <ValidationMessage>
                                    {validationErrors.volume.message}
                                </ValidationMessage>
                            )}
                        </GridItem>
                        <GridItem>
                            <FormField
                                label={t("common.weight")}
                                optional>
                                <InputNumber
                                    value={weight}
                                    onChange={setWeight}
                                    maxDecimals={2}
                                    after={<div className="pl-1 pr-2">{t("common.kg")}</div>}
                                    isInvalid={ValidationService.isFieldInvalid(validationErrors, 'weight')}
                                    isSubmitted={isSubmitted}
                                />
                            </FormField>
                            {isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'weight') && (
                                <ValidationMessage>
                                    {validationErrors.weight.message}
                                </ValidationMessage>
                            )}
                        </GridItem>
                    </Grid>
                </Grid>
            </Form>
        </ConfirmModal>
    )
}

export default FreightUnitModal
